import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Observable } from 'rxjs';
import { catchError, take } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';

import { AuthService } from '@services/auth.service';
import { TranslocoService } from '@jsverse/transloco';
import { SKIP_CONNECTIVITY_DIALOG_CLOSE } from './http-context-tokens';

export function tokenInterceptor(req: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<any>> {
    const auth = inject(AuthService);
    const toasts = inject(ToastrService);
    const transloco = inject(TranslocoService);
    const dialog = inject(MatDialog);

    // Only attach the session token to same-origin / our own API requests.
    // Attaching it to third-party URLs (e.g. ArcGIS) triggers a CORS preflight
    // that those servers won't allow because they don't whitelist x-session-token.
    const isSameOrigin = req.url.startsWith('/') || req.url.startsWith(window.location.origin);
    if (isSameOrigin && auth.apiToken) {
        req = req.clone({
            setHeaders: {
                'x-session-token': `${auth.apiToken}`
            }
        });
    }

    return next(req).pipe(
        catchError((err) => {
            if (err instanceof HttpErrorResponse) {
                // some api routes still return a 419 so we should check for both
                // EXCEPT: /api/login/resetPasswordInitiate uses 419 for "account not found" not auth failure
                const isPasswordResetEndpoint = req.url.includes('/api/login/resetPasswordInitiate');
                const isLogoutEndpoint = req.url.includes('/api/login/logout');
                const isSsoExchangeEndpoint = req.url.includes('/api/login/sso/exchange');

                if (
                    (err.status === 401 || err.status === 419) &&
                    !isPasswordResetEndpoint &&
                    !isLogoutEndpoint &&
                    !isSsoExchangeEndpoint &&
                    auth.user
                ) {
                    localStorage.removeItem('currentLibrary' + auth.user?.id);
                    localStorage.removeItem('currentBranch' + auth.user?.id);
                    auth.logout();
                }

                // When the API is temporarily unavailable, close any open overlays so
                // the user is not left interacting with stale dialogs.
                //
                // Status 0 is ambiguous: it covers both genuine network failures AND
                // requests aborted by Angular navigation / component teardown.
                // AuthService.performLogout() documents that navigation can abort
                // in-flight requests with status 0, which would incorrectly close an
                // open schedule dialog while the user remains authenticated.
                //
                // Guard: treat status 0 as a real outage ONLY when the browser
                // reports it is offline (navigator.onLine === false).  Status 503 is
                // an explicit server-side signal and always triggers closure.
                //
                // Callers that know a request may be intentionally aborted can opt
                // out entirely by setting SKIP_CONNECTIVITY_DIALOG_CLOSE on the
                // request context.
                const suppressClose = req.context.get(SKIP_CONNECTIVITY_DIALOG_CLOSE);
                const isConfirmedOutage =
                    err.status === 503 ||
                    (err.status === 0 && !navigator.onLine);
                if (isSameOrigin && isConfirmedOutage && auth.user && !suppressClose) {
                    dialog.closeAll();
                }

                if (err.status === 429) {
                    // Use selectTranslate so the translation file is loaded if it hasn't been yet;
                    // translate() synchronously returns the key when the active lang isn't loaded.
                    transloco
                        .selectTranslate('common.toasts.tooManyRequests')
                        .pipe(take(1))
                        .subscribe((msg) => toasts.error(msg));
                }
                if (err.status === 403) {
                    console.error(err);
                    // Use selectTranslate so the translation file is loaded if it hasn't been yet;
                    // translate() synchronously returns the key when the active lang isn't loaded.
                    transloco
                        .selectTranslate('common.toasts.forbidden')
                        .pipe(take(1))
                        .subscribe((msg) => toasts.error(msg));
                }
            }
            throw err;
        })
    );
}
